/* prettier-ignore-file */
import React, { useEffect, useState } from "react";
import { View, StyleSheet, Text, ActivityIndicator, TouchableOpacity } from "react-native";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getSettingsPath, getCollectionPath } from "../utils/dbPaths";
import RentalWaiver from "../components/RentalWaiver";
import EquipmentWaiver from "../components/EquipmentWaiver";
import { doc, getDoc, setDoc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { useNewBookingStore } from "../stores";
import { capitalizeWords } from "../utils/formatting";

const RentalWaiverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const storeWaiverData = useNewBookingStore((state) => state.waiverData);

  // Get bookingId from location state or URL search params
  const bookingIdFromState = location.state?.bookingId;
  const bookingIdFromParams = searchParams.get("bookingId");
  const bookingId = bookingIdFromState || bookingIdFromParams;

  // Reset the 5-minute draft-staleness clock when user enters data on waiver (touch store)
  const handleWaiverFormChange = () => {
    useNewBookingStore.getState().updateBookingData({});
  };

  // Get data from location state
  const customerNameFromState = location.state?.customerName || "";
  const hasBicycles = location.state?.hasBicycles !== false; // Default to true for backward compatibility
  const paymentUrl = location.state?.paymentUrl;
  const bookingDataFromState = location.state?.bookingData; // Booking data to save after waiver is signed
  const pendingBookingsPathFromState = location.state?.pendingBookingsPath; // Path to save booking
  const isOriginalBooker = location.state?.isOriginalBooker || false; // Track if coming from ViewBooking
  // Cash flow (booking wizard, staff only): sign the waiver, then return to the
  // wizard to confirm cash was received. No Stripe, so no paymentUrl is required.
  const cashPayment = location.state?.cashPayment || false;
  const returnTo = location.state?.returnTo;

  const [customerName, setCustomerName] = useState(customerNameFromState);
  const [legalStoreName, setLegalStoreName] = useState("Legal Store Name");
  const [legalStoreAddress, setLegalStoreAddress] = useState("");
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [hasExistingPayment, setHasExistingPayment] = useState(false);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [waiverSaved, setWaiverSaved] = useState(false); // Track if waiver was saved successfully
  const [error, setError] = useState(null);

  // Load customer info from Zustand store and settings
  useEffect(() => {
    const loadCustomerAndSettings = async () => {
      try {
        // If coming from ViewBooking (isOriginalBooker), load booking to check for payments
        if (isOriginalBooker && bookingId) {
          console.log("📋 Loading booking from ViewBooking to check for payments...");
          try {
            // Try to load from bookings collection
            const bookingsPath = getCollectionPath("bookings");
            const bookingRef = doc(db, bookingsPath, bookingId);
            const bookingSnap = await getDoc(bookingRef);
            
            if (bookingSnap.exists()) {
              const bookingData = bookingSnap.data();
              // Check if booking has payments
              const hasPayments = bookingData.payments && bookingData.payments.length > 0;
              setHasExistingPayment(hasPayments);
              console.log(`✅ Booking loaded. Has payments: ${hasPayments}`);
              
              // Check if waiver is already signed
              if (bookingData.waiverSigned || (bookingData.waiverData && bookingData.waiverData.length > 0)) {
                setWaiverSaved(true);
                console.log("✅ Waiver already signed in booking");
              }
            } else {
              console.warn("⚠️ Booking not found in bookings collection");
            }
          } catch (bookingError) {
            console.error("❌ Error loading booking:", bookingError);
            // Continue anyway - don't block the waiver page
          }
        }
        
        // Get customer info from Zustand store instead of Firestore
        const store = useNewBookingStore.getState();
        const storeFirstName = store.firstName || "";
        const storeLastName = store.lastName || "";
        
        // Use customer name from state if provided, otherwise from store
        let resolvedCustomerName = customerNameFromState;
        if (!resolvedCustomerName || resolvedCustomerName.trim() === "") {
          resolvedCustomerName = capitalizeWords(`${storeFirstName} ${storeLastName}`.trim());
        }

        // Customer name is required - fail gracefully if cannot be resolved
        if (!resolvedCustomerName || resolvedCustomerName.trim() === "") {
          setError("Customer name could not be resolved. Please go back and fill in your information.");
          setLoadingBooking(false);
          return;
        }

        setCustomerName(resolvedCustomerName);
        console.log("✅ Customer name from store/state:", resolvedCustomerName);
        
        // Check if waiver is already signed in store (only if not from ViewBooking)
        if (!isOriginalBooker && store.waiverSigned && store.waiverData) {
          setWaiverSaved(true);
          console.log("✅ Waiver already signed in store");
        }
        
        // Load settings
        const settingsPath = getSettingsPath();
        console.log("📋 Loading legal store info for waiver...");
        console.log("  - Settings path:", settingsPath);
        
        const nameDoc = await getDoc(doc(db, settingsPath, "legalStoreName"));
        const addressDoc = await getDoc(doc(db, settingsPath, "legalStoreAddress"));
        const policyDoc = await getDoc(doc(db, settingsPath, "cancellationPolicy"));
        
        console.log("  - Name doc exists:", nameDoc.exists());
        console.log("  - Address doc exists:", addressDoc.exists());
        console.log("  - Policy doc exists:", policyDoc.exists());
        
        if (nameDoc.exists()) {
          const name = nameDoc.data().name || "Legal Store Name";
          console.log("  - Loaded legal name:", name);
          setLegalStoreName(name);
        }
        
        if (addressDoc.exists()) {
          const address = addressDoc.data().address || "";
          console.log("  - Loaded legal address:", address);
          setLegalStoreAddress(address);
        } else {
          console.log("  - No legal address document in database");
        }
        
        if (policyDoc.exists()) {
          const policy = policyDoc.data().policy || "";
          console.log("  - Loaded cancellation policy:", policy);
          setCancellationPolicy(policy);
        }
      } catch (error) {
        console.error("❌ Error loading booking and settings:", error);
        setError(`Error loading waiver: ${error.message || "Unknown error"}`);
      } finally {
        setLoadingBooking(false);
      }
    };
    
    loadCustomerAndSettings();
    window.scrollTo(0, 0);
  }, [customerNameFromState, isOriginalBooker, bookingId]);

  // Handler for saving waiver only (no payment)
  const handleSaveWaiverOnly = async (data) => {
    console.log("💾 Saving waiver...");
    console.log("📋 Waiver data:", data);
    console.log("📋 Coming from ViewBooking:", isOriginalBooker);
    console.log("📋 Booking ID:", bookingId);
    
    try {
      // Create waiver object with isOriginalBooker flag
      const waiverEntry = {
        ...data,
        isOriginalBooker: true,
        signedAt: new Date(),
      };
      
      // If coming from ViewBooking, save to Firestore booking
      if (isOriginalBooker && bookingId) {
        console.log("💾 Saving waiver to existing booking in Firestore...");
        
        try {
          // Try to load from bookings collection
          const bookingsPath = getCollectionPath("bookings");
          const bookingRef = doc(db, bookingsPath, bookingId);
          const bookingSnap = await getDoc(bookingRef);
          
          if (bookingSnap.exists()) {
            const existingWaiverData = bookingSnap.data().waiverData || [];
            
            // Create waiver entry with proper Timestamp conversion
            const waiverEntryForFirestore = {
              ...waiverEntry,
              signedAt: waiverEntry.signedAt instanceof Date
                ? Timestamp.fromDate(waiverEntry.signedAt)
                : Timestamp.now(),
            };
            
            // Append waiver entry to existing waiverData array
            const updatedWaiverData = [...existingWaiverData, waiverEntryForFirestore];
            
            await updateDoc(bookingRef, {
              waiverSigned: true,
              waiverData: updatedWaiverData,
              updatedAt: Timestamp.now(),
            });
            
            console.log("✅ Waiver saved to existing booking in Firestore");
            
            // Mark waiver as saved
            setWaiverSaved(true);
            
            // Show success message
            alert("✅ Waiver saved successfully! You can now view your booking.");
            
            // Navigate back to view booking page
            navigate(`/view-booking/${bookingId}`);
          } else {
            throw new Error("Booking not found in Firestore");
          }
        } catch (firestoreError) {
          console.error("❌ Error saving waiver to Firestore:", firestoreError);
          alert("Error: Failed to save waiver to booking. Please try again.");
          throw firestoreError;
        }
      } else {
        // Not from ViewBooking - save to Zustand store (new booking flow)
        console.log("💾 Saving waiver to Zustand store...");
        const { setWaiverSigned, setWaiverData } = useNewBookingStore.getState();
        setWaiverSigned(true);
        setWaiverData(waiverEntry);
        
        // Mark waiver as saved
        setWaiverSaved(true);
        console.log("✅ Waiver saved to Zustand store successfully!");
      }
    } catch (error) {
      console.error("❌ Error saving waiver:", error);
      alert("Error: Failed to save waiver. Please try again.");
      throw error; // Throw to keep modal visible
    }
  };

  // Handler for saving waiver and continuing to payment
  const handleWaiverSignedWithPayment = async (data) => {
    console.log("🎯 RentalWaiverPage - handleWaiverSignedWithPayment called!");
    console.log("🔄 Waiver signed, saving to store and navigating to Stripe checkout");
    console.log("📋 Waiver data:", data);
    console.log("💳 Payment URL:", paymentUrl);
    console.log("🆔 Booking ID:", bookingId);
    
    // Cash bookings have no Stripe session, so there is no payment URL to require.
    // Everything below (persisting the waiver into the pending booking) is shared;
    // only the final navigation differs — see the cash branch at the end.
    if (!cashPayment && !paymentUrl) {
      console.error("❌ No payment URL! Cannot proceed to Stripe.");
      alert("Error: No payment URL found. Please go back and try again.");
      return;
    }

    if (!bookingId) {
      console.error("❌ No booking ID! Cannot proceed to payment.");
      alert("Error: No booking ID found. Please go back and try again.");
      return;
    }
    
    // Create waiver object with isOriginalBooker flag
    const waiverEntry = {
      ...data,
      isOriginalBooker: true,
      signedAt: new Date(),
    };
    
    // Save to Zustand store so StripeCheckout's pending-booking update and a later
    // same-customer waiver skip both work. NOT for cash: the wizard's cash flow has
    // already cleared the draft for the next walk-up customer, and writing this
    // signature back would leave the previous customer's waiver sitting in an empty
    // draft on the shared register.
    if (!cashPayment) {
      try {
        const { setWaiverSigned, setWaiverData } = useNewBookingStore.getState();
        setWaiverSigned(true);
        setWaiverData(waiverEntry);
        console.log("✅ Waiver saved to Zustand store");
      } catch (error) {
        console.error("❌ Error saving waiver to store:", error);
      }
    }
    
    // Save booking to Firestore with waiver data included
    try {
      console.log("📝 Saving booking to Firestore with waiver data...");
      
      // Determine pendingBookings path (use from state if provided, otherwise get from context)
      const pendingBookingsPath = pendingBookingsPathFromState || getCollectionPath("pendingBookings");
      const bookingRef = doc(db, pendingBookingsPath, bookingId);
      
      // Check if booking already exists in Firestore
      const bookingSnap = await getDoc(bookingRef);
      
      if (bookingSnap.exists()) {
        // Booking already exists (legacy flow) - update with waiver data
        console.log("📝 Booking exists, updating with waiver data...");
        const existingWaiverData = bookingSnap.data().waiverData || [];
        
        // Create waiver entry with proper Timestamp conversion
        const waiverEntryForFirestore = {
          ...waiverEntry,
          signedAt: waiverEntry.signedAt instanceof Date
            ? Timestamp.fromDate(waiverEntry.signedAt)
            : Timestamp.now(),
        };
        
        // Append waiver entry to existing waiverData array
        const updatedWaiverData = [...existingWaiverData, waiverEntryForFirestore];
        
        await updateDoc(bookingRef, {
          waiverSigned: true,
          waiverData: updatedWaiverData,
          updatedAt: Timestamp.now(),
        });
        
        console.log("✅ Waiver saved to existing booking in Firestore");
      } else if (bookingDataFromState) {
        // Booking doesn't exist yet (new flow) - create booking with waiver included
        console.log("📝 Creating new booking with waiver data...");
        
        // Create waiver entry with proper Timestamp conversion
        const waiverEntryForFirestore = {
          ...waiverEntry,
          signedAt: waiverEntry.signedAt instanceof Date
            ? Timestamp.fromDate(waiverEntry.signedAt)
            : Timestamp.now(),
        };
        
        // Convert ISO string timestamps back to Firestore Timestamps
        const finalBookingData = {
          ...bookingDataFromState,
          // Convert ISO string timestamps back to Firestore Timestamps
          createdAt: bookingDataFromState.createdAt ? (typeof bookingDataFromState.createdAt === 'string' ? Timestamp.fromDate(new Date(bookingDataFromState.createdAt)) : bookingDataFromState.createdAt) : Timestamp.now(),
          startDate: bookingDataFromState.startDate ? (typeof bookingDataFromState.startDate === 'string' ? Timestamp.fromDate(new Date(bookingDataFromState.startDate)) : bookingDataFromState.startDate) : Timestamp.now(),
          endDate: bookingDataFromState.endDate ? (typeof bookingDataFromState.endDate === 'string' ? Timestamp.fromDate(new Date(bookingDataFromState.endDate)) : bookingDataFromState.endDate) : Timestamp.now(),
          // Convert createdBy timestamp if it's an ISO string
          createdBy: bookingDataFromState.createdBy ? {
            ...bookingDataFromState.createdBy,
            timestamp: typeof bookingDataFromState.createdBy.timestamp === 'string' ? Timestamp.fromDate(new Date(bookingDataFromState.createdBy.timestamp)) : (bookingDataFromState.createdBy.timestamp || Timestamp.now()),
          } : bookingDataFromState.createdBy,
          waiverSigned: true,
          waiverData: [waiverEntryForFirestore],
          updatedAt: Timestamp.now(),
        };
        
        await setDoc(bookingRef, finalBookingData);
        
        console.log("✅ Booking created with waiver data in Firestore");
      } else {
        // Nothing to update and nothing to create: proceeding would take payment
        // for a booking the webhook can never find.
        throw new Error("No booking data available to save");
      }
    } catch (error) {
      console.error("❌ Error saving booking/waiver to Firestore:", error);
      // For NEW bookings, do NOT proceed to payment: Stripe would capture money
      // for a booking that doesn't exist in pendingBookings — the webhook
      // confirms nothing and the customer is charged with no booking anywhere.
      // For cash, the desk screen would complete a booking with no waiver on
      // record. Blocking here makes a retry a single tap.
      //
      // isOriginalBooker flows are exempt: their booking already exists in the
      // `bookings` collection, so the pendingBookings write is not load-bearing
      // for payment and the legacy continue is correct.
      if (!isOriginalBooker) {
        alert(
          "Your waiver was signed, but the booking could not be saved. " +
            "Please check your connection and press the sign button again."
        );
        return;
      }
    }
    
    // Cash: the booking is now saved to pendingBookings with the signed waiver, but no
    // money has changed hands. Hand off to the cash desk screen, where staff record the
    // tender and confirm — that is what promotes it to a real booking.
    if (cashPayment) {
      navigate(`/cash-payment/${bookingId}`, {
        state: { pendingBookingsPath: pendingBookingsPathFromState, returnTo },
      });
      return;
    }

    // Navigate to Stripe checkout page. Pass referrer so that if user hits back from
    // Stripe they skip the waiver and go to the previous step. Callers that know
    // where the flow started (the booking wizard) pass their own referrer.
    const backFromStripeReferrer =
      location.state?.referrer ||
      (bookingId && isOriginalBooker ? `/view-booking/${bookingId}` : "/rental-bookings");
    console.log("🚀 Navigating to Stripe checkout...");
    navigate("/stripe-checkout", { 
      state: { 
        paymentUrl: paymentUrl,
        bookingId: bookingId,
        referrer: backFromStripeReferrer,
      } 
    });
    console.log("✅ Navigation triggered");
  };

  const handleCancel = () => {
    // Navigate back based on context
    if (bookingId) {
      navigate(`/view-booking/${bookingId}`);
    } else {
      navigate("/rental-bookings");
    }
  };

  if (loadingBooking) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={styles.loadingText}>Loading waiver...</Text>
        </View>
      </View>
    );
  }

  // Show error if customer name could not be resolved or booking not found
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <View style={styles.errorContent}>
          <Text style={styles.errorTitle}>⚠️ Unable to Load Waiver</Text>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.errorButton} onPress={handleCancel}>
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Ensure customer name is available before rendering waiver
  if (!customerName || customerName.trim() === "") {
    return (
      <View style={styles.errorContainer}>
        <View style={styles.errorContent}>
          <Text style={styles.errorTitle}>⚠️ Unable to Load Waiver</Text>
          <Text style={styles.errorText}>Customer name is required but could not be resolved. Please contact support.</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.errorButton} onPress={handleCancel}>
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {hasBicycles ? (
        <RentalWaiver
          customerName={customerName}
          onWaiverSigned={handleWaiverSignedWithPayment}
          onSaveWaiverOnly={handleSaveWaiverOnly}
          onCancel={handleCancel}
          legalStoreName={legalStoreName}
          legalStoreAddress={legalStoreAddress}
          cancellationPolicyProp={cancellationPolicy}
          hasExistingPayment={hasExistingPayment}
          isOriginalBooker={isOriginalBooker}
          bookingId={bookingId}
          paymentUrl={paymentUrl}
          waiverSaved={waiverSaved}
          initialWaiverData={storeWaiverData}
          onFormChange={handleWaiverFormChange}
        />
      ) : (
        <EquipmentWaiver
          customerName={customerName}
          onWaiverSigned={handleWaiverSignedWithPayment}
          onSaveWaiverOnly={handleSaveWaiverOnly}
          onCancel={handleCancel}
          legalStoreName={legalStoreName}
          legalStoreAddress={legalStoreAddress}
          cancellationPolicyProp={cancellationPolicy}
          hasExistingPayment={hasExistingPayment}
          isOriginalBooker={isOriginalBooker}
          bookingId={bookingId}
          paymentUrl={paymentUrl}
          waiverSaved={waiverSaved}
          initialWaiverData={storeWaiverData}
          onFormChange={handleWaiverFormChange}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F6F8F7",
  },
  loadingContent: {
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    color: "#5C6B68",
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F6F8F7",
    padding: 20,
  },
  errorContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    maxWidth: 500,
    width: "100%",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#C4453D",
    marginBottom: 12,
    textAlign: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#2E3A38",
    marginBottom: 20,
    textAlign: "center",
    lineHeight: 22,
  },
  errorButton: {
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    padding: 14,
    alignItems: "center",
  },
  errorButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default RentalWaiverPage;

